"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NotificationItem } from "./NotificationItem";
import type { CNSNotification } from "@/models/cns";

/**
 * Inbox content rendered inside the bell {@code Popover}. Splits items
 * into Today / Earlier groups, formats relative time via {@code date-fns},
 * and offers a "Mark all read" action.
 *
 * <p>Virtualization (via {@code @tanstack/react-virtual}) is intentionally
 * deferred to a follow-up — server-side caps the page at 50 so the panel
 * stays responsive even without virtualisation.
 */
export function NotificationPanel(t0) {
  const $ = _c(18);
  const {
    onItemSelected
  } = t0;
  const notifications = useNotificationStore(_temp);
  const loading = useNotificationStore(_temp2);
  const error = useNotificationStore(_temp3);
  const unreadCount = useNotificationStore(_temp4);
  const markAllRead = useNotificationStore(_temp5);
  let t1;
  if ($[0] !== notifications) {
    t1 = groupByDay(notifications);
    $[0] = notifications;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    today,
    earlier
  } = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h3 className="text-sm font-semibold">Notifications</h3>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = unreadCount === 0;
  let t4;
  if ($[3] !== markAllRead) {
    t4 = () => void markAllRead();
    $[3] = markAllRead;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <div className="flex items-center justify-between px-4 py-3 border-b">{t2}<Button type="button" variant="ghost" size="sm" className="h-7 text-xs" disabled={t3} onClick={t4}>Mark all read</Button></div>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== earlier || $[9] !== error || $[10] !== loading || $[11] !== notifications || $[12] !== onItemSelected || $[13] !== today) {
    t6 = <ScrollArea className="flex-1 min-h-0">{loading && notifications.length === 0 ? <p className="px-4 py-8 text-center text-sm text-muted-foreground">Loading…</p> : error ? <p className="px-4 py-8 text-center text-sm text-destructive">{error}</p> : notifications.length === 0 ? <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up.</p> : <div className="py-1">{today.length > 0 && <NotificationGroup label="Today" items={today} onItemSelected={onItemSelected} />}{earlier.length > 0 && <NotificationGroup label="Earlier" items={earlier} onItemSelected={onItemSelected} />}</div>}</ScrollArea>;
    $[8] = earlier;
    $[9] = error;
    $[10] = loading;
    $[11] = notifications;
    $[12] = onItemSelected;
    $[13] = today;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <div className="flex flex-col h-full max-h-[70vh]">{t5}{t6}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp5(s_3) {
  return s_3.markAllRead;
}
function _temp4(s_2) {
  return s_2.unreadCount;
}
function _temp3(s_1) {
  return s_1.error;
}
function _temp2(s_0) {
  return s_0.loading;
}
function _temp(s) {
  return s.notifications;
}
function NotificationGroup(t0) {
  const $ = _c(12);
  const {
    label,
    items,
    onItemSelected
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="px-4 pt-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== items || $[3] !== onItemSelected) {
    let t3;
    if ($[5] !== onItemSelected) {
      t3 = n => <li key={n.id}><NotificationItem notification={n} onSelected={onItemSelected} /></li>;
      $[5] = onItemSelected;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = items.map(t3);
    $[2] = items;
    $[3] = onItemSelected;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <ul className="divide-y">{t2}</ul>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3) {
    t4 = <div>{t1}{t3}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function groupByDay(items: CNSNotification[]) {
  const today: CNSNotification[] = [];
  const earlier: CNSNotification[] = [];
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  for (const n of items) {
    const t = new Date(n.createdAt);
    if (t.getTime() >= startOfToday.getTime()) {
      today.push(n);
    } else {
      earlier.push(n);
    }
  }
  return {
    today,
    earlier
  };
}